<script>
import axios from "axios";
import { toast } from "vue3-toastify";

export default {
  name: "admin-ll-prices",
  props: ['currentuser', 'admin'],
  data() {
    return {
      prices: [],
      allLectors: [],
      availableLectors: [],
      selectedLectorId: null,
      editedPrices: new Set(),
      savingPrices: new Set(),
      loading: false,
      loadingLectors: false
    };
  },
  mounted() {
    this.loadPrices();
    this.loadAllLectors();
  },
  methods: {
    async loadPrices() {
      this.loading = true;
      try {
        const response = await axios.get('/ll/prices.json');
        this.prices = response.data;
        this.updateAvailableLectors();
      } catch (error) {
        console.error('Chyba při načítání ceníků:', error);
        toast.error('Nepodařilo se načíst ceníky');
      } finally {
        this.loading = false;
      }
    },

    async loadAllLectors() {
      this.loadingLectors = true;
      try {
        // Načteme všechny lektory z conversation clubs API
        const response = await axios.get('/conversation-clubs/lectors.json', {
          params: {
            per: 100
          }
        });
        this.allLectors = response.data.items || [];
        this.updateAvailableLectors();
      } catch (error) {
        console.error('Chyba při načítání lektorů:', error);
        toast.error('Nepodařilo se načíst seznam lektorů');
      } finally {
        this.loadingLectors = false;
      }
    },

    updateAvailableLectors() {
      // Filtrujeme lektory, kteří ještě nemají nastavený ceník
      const usedLectorIds = this.prices.map(p => p.lector_id);
      this.availableLectors = this.allLectors.filter(l => !usedLectorIds.includes(l.id));
    },

    addLector() {
      if (!this.selectedLectorId) {
        toast.warning('Vyberte lektora');
        return;
      }

      const lector = this.allLectors.find(l => l.id === parseInt(this.selectedLectorId));
      if (!lector) {
        toast.error('Lektor nenalezen');
        return;
      }

      // Přidáme nový řádek s výchozími cenami
      const newPrice = {
        id: null,
        lector_id: lector.id,
        lector: lector,
        price_30_min: 0,
        price_45_min: 0,
        price_60_min: 0,
        price_30_min_eur: 0,
        price_45_min_eur: 0,
        price_60_min_eur: 0
      };

      this.prices.push(newPrice);
      this.markAsEdited(lector.id);
      this.updateAvailableLectors();
      this.selectedLectorId = null;

      toast.info('Lektor přidán, nezapomeňte uložit');
    },

    markAsEdited(lectorId) {
      this.editedPrices.add(lectorId);
    },

    async savePrice(price) {
      if (this.savingPrices.has(price.lector_id)) {
        return;
      }

      this.savingPrices.add(price.lector_id);

      try {
        const payload = {
          ll_price: {
            lector_id: price.lector_id,
            price_30_min: parseFloat(price.price_30_min) || 0,
            price_45_min: parseFloat(price.price_45_min) || 0,
            price_60_min: parseFloat(price.price_60_min) || 0,
            price_30_min_eur: parseFloat(price.price_30_min_eur) || 0,
            price_45_min_eur: parseFloat(price.price_45_min_eur) || 0,
            price_60_min_eur: parseFloat(price.price_60_min_eur) || 0
          }
        };

        let response;
        if (price.id) {
          // Update - používáme ID ceníku, ne lector_id
          response = await axios.put(`/ll/prices/${price.id}.json`, payload);
        } else {
          // Create - POST na collection endpoint
          response = await axios.post(`/ll/prices.json`, payload);
        }

        const index = this.prices.findIndex(p => p.lector_id === price.lector_id);
        if (index !== -1) {
          this.prices[index] = response.data;
        }

        this.editedPrices.delete(price.lector_id);
        toast.success('Ceník byl úspěšně uložen');
      } catch (error) {
        console.error('Chyba při ukládání ceníku:', error);
        toast.error('Nepodařilo se uložit ceník: ' + (error.response?.data?.errors?.join(', ') || error.message));
      } finally {
        this.savingPrices.delete(price.lector_id);
      }
    },

    async deletePrice(price) {
      if (!confirm(`Opravdu chcete smazat ceník pro ${this.getLectorName(price.lector)}?`)) {
        return;
      }

      try {
        if (price.id) {
          await axios.delete(`/ll/prices/${price.id}.json`);
        }

        const index = this.prices.findIndex(p => p.lector_id === price.lector_id);
        if (index !== -1) {
          this.prices.splice(index, 1);
        }

        this.editedPrices.delete(price.lector_id);
        this.updateAvailableLectors();
        toast.success('Ceník byl úspěšně smazán');
      } catch (error) {
        console.error('Chyba při mazání ceníku:', error);
        toast.error('Nepodařilo se smazat ceník');
      }
    },

    cancelEdit(price) {
      if (!price.id) {
        // Nový záznam, který nebyl uložen - odstraníme ho
        const index = this.prices.findIndex(p => p.lector_id === price.lector_id);
        if (index !== -1) {
          this.prices.splice(index, 1);
        }
        this.updateAvailableLectors();
      } else {
        // Existující záznam - znovu načteme data
        this.loadPrices();
      }
      this.editedPrices.delete(price.lector_id);
    },

    isEdited(lectorId) {
      return this.editedPrices.has(lectorId);
    },

    isSaving(lectorId) {
      return this.savingPrices.has(lectorId);
    },

    formatPrice(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2);
    },

    getLectorName(lector) {
      if (!lector) return 'Neznámý lektor';
      return `${lector.first_name || ''} ${lector.last_name || ''}`.trim() || lector.email;
    }
  }
}
</script>

<template>
  <div class="container-fluid mt-4">
    <h3 class="mb-4">Lekce s lektory - ceny v eshopu</h3>

    <!-- Přidání nového lektora -->
    <div class="card mb-3">
      <div class="card-body">
        <h5>Přidat lektora</h5>
        <div class="row align-items-end">
          <div class="col-md-6">
            <label>Vyberte lektora</label>
            <select v-model="selectedLectorId" class="form-control" :disabled="loadingLectors">
              <option :value="null">-- Vyberte lektora --</option>
              <option v-for="lector in availableLectors" :key="lector.id" :value="lector.id">
                {{ getLectorName(lector) }} ({{ lector.email }})
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <button
              @click="addLector"
              class="btn btn-primary"
              :disabled="!selectedLectorId || loadingLectors"
            >
              <span class="material-icons-outlined" style="font-size: 16px; vertical-align: middle;">add</span>
              Přidat lektora
            </button>
          </div>
        </div>
        <small class="text-muted mt-2 d-block" v-if="availableLectors.length === 0 && !loadingLectors">
          Všichni lektoři již mají nastavený ceník
        </small>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Načítání...</span>
      </div>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th style="width: 20%">Lektor</th>
                <th style="width: 10%">30 min (Kč)</th>
                <th style="width: 10%">30 min (EUR)</th>
                <th style="width: 10%">45 min (Kč)</th>
                <th style="width: 10%">45 min (EUR)</th>
                <th style="width: 10%">60 min (Kč)</th>
                <th style="width: 10%">60 min (EUR)</th>
                <th style="width: 20%">Akce</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="prices.length === 0">
                <td colspan="8" class="text-center text-muted py-4">
                  Zatím nejsou nastaveny žádné ceníky. Přidejte prvního lektora výše.
                </td>
              </tr>
              <tr v-for="price in prices" :key="price.lector_id" :class="{ 'table-warning': isEdited(price.lector_id) }">
                <td>
                  <strong>{{ getLectorName(price.lector) }}</strong><br>
                  <small class="text-muted">{{ price.lector?.email }}</small>
                </td>
                <td>
                  <input
                    type="number"
                    v-model="price.price_30_min"
                    class="form-control"
                    step="0.01"
                    min="0"
                    @input="markAsEdited(price.lector_id)"
                  >
                </td>
                <td>
                  <input
                    type="number"
                    v-model="price.price_30_min_eur"
                    class="form-control"
                    step="0.01"
                    min="0"
                    @input="markAsEdited(price.lector_id)"
                  >
                </td>
                <td>
                  <input
                    type="number"
                    v-model="price.price_45_min"
                    class="form-control"
                    step="0.01"
                    min="0"
                    @input="markAsEdited(price.lector_id)"
                  >
                </td>
                <td>
                  <input
                    type="number"
                    v-model="price.price_45_min_eur"
                    class="form-control"
                    step="0.01"
                    min="0"
                    @input="markAsEdited(price.lector_id)"
                  >
                </td>
                <td>
                  <input
                    type="number"
                    v-model="price.price_60_min"
                    class="form-control"
                    step="0.01"
                    min="0"
                    @input="markAsEdited(price.lector_id)"
                  >
                </td>
                <td>
                  <input
                    type="number"
                    v-model="price.price_60_min_eur"
                    class="form-control"
                    step="0.01"
                    min="0"
                    @input="markAsEdited(price.lector_id)"
                  >
                </td>
                <td>
                  <div class="btn-group" role="group">
                    <button
                      v-if="isEdited(price.lector_id)"
                      @click="savePrice(price)"
                      class="btn btn-sm btn-success"
                      :disabled="isSaving(price.lector_id)"
                      title="Uložit"
                    >
                      <span v-if="isSaving(price.lector_id)" class="spinner-border spinner-border-sm"></span>
                      <span v-else class="material-icons-outlined" style="font-size: 16px;">save</span>
                    </button>
                    <button
                      v-if="isEdited(price.lector_id)"
                      @click="cancelEdit(price)"
                      class="btn btn-sm btn-secondary"
                      :disabled="isSaving(price.lector_id)"
                      title="Zrušit"
                    >
                      <span class="material-icons-outlined" style="font-size: 16px;">close</span>
                    </button>
                    <button
                      @click="deletePrice(price)"
                      class="btn btn-sm btn-danger"
                      :disabled="isSaving(price.lector_id)"
                      title="Smazat"
                    >
                      <span class="material-icons-outlined" style="font-size: 16px;">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card mt-3">
      <div class="card-body">
        <h5>Informace</h5>
        <ul class="mb-0">
          <li>Vyberte lektora ze seznamu a přidejte ho do tabulky</li>
          <li>Při změně ceny se zvýrazní řádek žlutě a objeví se tlačítko "Uložit"</li>
          <li>Ceny zadávejte v korunách (Kč) a v eurech (EUR)</li>
          <li>EUR ceny jsou nepovinné - můžete vyplnit pouze Kč nebo obě měny</li>
          <li>Změny se ukládají jednotlivě pro každého lektora</li>
          <li>Tlačítkem "Smazat" odstraníte celý ceník lektora</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table-warning {
  background-color: #fff3cd !important;
  transition: background-color 0.3s ease;
}

input[type="number"] {
  max-width: 150px;
}

.material-icons-outlined {
  font-size: 18px;
  vertical-align: middle;
}

.btn-sm .material-icons-outlined {
  font-size: 16px;
}

.btn-group {
  gap: 4px;
}
</style>
